import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin, Globe, ExternalLink } from "lucide-react";
import { LOGO_URL } from "@/data/content";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { telHref, waHref } from "@/lib/links";

const COLS = [
  {
    title: "School of Basic Studies",
    links: [
      ["About", "/about"],
      ["JUPEB", "/jupeb"],
      ["IJMB", "/ijmb"],
      ["Programmes", "/programmes"],
      ["Admissions", "/admissions"],
    ],
  },
  {
    title: "Information",
    links: [
      ["Eligibility", "/admissions"],
      ["Direct Entry", "/programmes"],
      ["Subject Combinations", "/programmes"],
      ["Student Information", "/student-information"],
      ["FAQs", "/faqs"],
      ["Announcements", "/announcements"],
    ],
  },
];

export function Footer() {
  const { config } = useSiteConfig();
  return (
    <footer className="relative bg-[#0A1E4E] text-blue-100/70 paper-grain" data-testid="site-footer">
      <div className="h-1 w-full" style={{ background: "linear-gradient(90deg,#D4AF37,#F5B301,#D4AF37)" }} />
      <div className="container-x py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="IAUE crest" className="h-14 w-14 object-contain" />
              <div>
                <div className="font-serif text-white font-bold leading-tight">School of Basic Studies</div>
                <div className="text-[11px] uppercase tracking-widest text-[#D4AF37]">IAUE • Port Harcourt</div>
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed">
              A stronger foundation for your university journey through structured JUPEB & IJMB academic preparation.
            </p>
            <a
              href={`https://${config.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] hover:underline"
              data-testid="footer-website-link"
            >
              <Globe size={15} /> {config.website} <ExternalLink size={13} />
            </a>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={label + to}>
                    <Link to={to} className="text-sm hover:text-[#D4AF37] transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white mb-4">Contact & Location</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={telHref(config.phonePrimary)} className="flex items-center gap-2 hover:text-[#D4AF37]" data-testid="footer-call-primary">
                  <Phone size={15} className="text-[#D4AF37]" /> {config.phonePrimary}
                </a>
              </li>
              {(config.phones || []).map((p) => (
                <li key={p}>
                  <a href={telHref(p)} className="flex items-center gap-2 hover:text-[#D4AF37]">
                    <Phone size={15} className="opacity-40" /> {p}
                  </a>
                </li>
              ))}
              <li>
                <a href={waHref(config.whatsapp)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#D4AF37]" data-testid="footer-whatsapp">
                  <MessageCircle size={15} className="text-[#D4AF37]" /> WhatsApp
                </a>
              </li>
              <li>
                <a href={`mailto:${config.email}`} className="flex items-center gap-2 hover:text-[#D4AF37]" data-testid="footer-email">
                  <Mail size={15} className="text-[#D4AF37]" /> {config.email}
                </a>
              </li>
              <li className="flex items-start gap-2 pt-1">
                <MapPin size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <span>St. John's Campus, Aba Road, Port Harcourt, Rivers State</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>© {new Date().getFullYear()} Ignatius Ajuru University of Education. All Rights Reserved.</p>
          <Link to="/admin" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-admin-link">
            Admin Portal
          </Link>
        </div>
      </div>
    </footer>
  );
}
